export default class AuthService {
  constructor ($log, $state, $q, authConfig, ApiService, PrimusService, LocalStorageService, RavenService, StateService, CONFIGURATION) {
    'ngInject'
    this._$log = $log
    this._$state = $state
    this._$q = $q
    this._authConfig = authConfig
    this._ApiService = ApiService
    this._PrimusService = PrimusService
    this._LocalStorageService = LocalStorageService
    this._RavenService = RavenService
    this._StateService = StateService
    this._stateSubscribed = false
    this._b2cConfig = (CONFIGURATION && CONFIGURATION.b2c) || {}
    this._redirectPromise = null
    this._authRedirectInFlight = false
    this._logoutInProgress = false

    // Setup subscription to auth change events
    this._ApiService.on('auth/isAuthenticated', (...args) => this._authChange('isAuthenticated', ...args))
    this._ApiService.on('auth/loginB2C', (...args) => this._authChange('loginB2C', ...args))
    this._ApiService.on('auth/logout', (...args) => this._authChange('logout', ...args))

    // Check auth state when connection opens
    PrimusService.on('open', () => this._ApiService.call('auth/isAuthenticated'))

    // Handle any pending B2C redirect (e.g. auth code in URL)
    this._ensureRedirectHandled().catch(() => {})
  }

  _authChange (event, err, authData) {
    if (err) {
      this._$log.error(`AuthService: Event '${event}' error`, err)
      this._authClear()
      return
    }

    this._$log.info(`AuthService: Event '${event}'`, authData)

    // Login or authenticated
    if (event === 'loginB2C' || (event === 'isAuthenticated' && authData.authenticated)) {
      let delay = 0
      if (event === 'loginB2C') {
        delay = 1000
      }
      // Patch auth data
      const mergedAuthData = Object.assign({}, this._LocalStorageService.get('authdata'), authData)
      this._LocalStorageService.set('authdata', mergedAuthData)

      // Ensure async operations are completed before next steps
      this._$q.resolve()
        .then(() => this._$q((resolve) => setTimeout(resolve, delay)))
        .then(() => {
          // Set user context for Raven
          this._RavenService.setUserContext(mergedAuthData)
          // Subscribe to full state
          if (!this._stateSubscribed) {
            this._StateService._authenticatedSubscribeToFullState()
            this._stateSubscribed = true
          }

          // Redirect to authed state if on not-authed state or redirect handler
          if (this._$state.current.name === this._authConfig.notAuthenticatedState ||
            this._$state.current.name === 'loginRedirect') {
            this._$state.go(this._authConfig.authenticatedInitialState)
          }
        })
    } else if (event === 'isAuthenticated') {
      // Unauthenticated: only update local auth state, no navigation.
      // Callers (ensureAuthenticated, route guards) decide how to redirect.
      this._LocalStorageService.set('authdata', null)
      this._RavenService.setUserContext()
    } else {
      this._authClear()
    }
  }

  _authClear () {
    this._$log.info('AuthService: Clearing auth')

    // Clear auth data
    this._LocalStorageService.set('authdata', null)

    // Clear user context for Raven
    this._RavenService.setUserContext()

    if (this._isAuthRedirectInFlight() || this._logoutInProgress) {
      return
    }

    // Redirect to not-authenticated state, if not there
    if (this._$state.current.name !== this._authConfig.notAuthenticatedState) {
      this._$state.go(this._authConfig.notAuthenticatedState, { reload: true })
    }
  }

  logout () {
    this._logoutInProgress = true
    return this._ApiService.call('auth/logout')
      .catch(err => {
        this._$log.warn('AuthService: Server logout failed', err)
      })
      .finally(() => {
        const logoutUrl = this._buildLogoutUrl()
        if (logoutUrl) {
          this._LocalStorageService.set('authdata', null)
          this._RavenService.setUserContext()
          window.location.href = logoutUrl
          return
        }

        this._logoutInProgress = false
        if (this._$state.current.name !== this._authConfig.notAuthenticatedState) {
          this._$state.go(this._authConfig.notAuthenticatedState, { reload: true })
        }
      })
  }

  get isAuthenticated () {
    const authData = this._LocalStorageService.get('authdata')
    return (authData && authData.authenticated) || false
  }

  ensureAuthenticated ({ skipRedirect = false } = {}) {
    return this._ensureRedirectHandled()
      .then(() => this._waitForPrimusConnection())
      .then(() => this._ApiService.call('auth/isAuthenticated'))
      .then(authData => {
        if (authData && authData.authenticated) {
          // Don't call _authChange manually here — the ApiService.on('auth/isAuthenticated')
          // subscription already handles it, avoiding double side-effects.
          return true
        }
        if (!skipRedirect && !this._isAuthRedirectInFlight()) {
          this._authClear()
        }
        return false
      })
      .catch(err => {
        this._$log.warn('AuthService: ensureAuthenticated failed', err)
        return false
      })
  }

  loginRedirect () {
    if (!this._b2cConfig.authority || !this._b2cConfig.clientId) {
      return this._$q.reject(new Error('Azure AD B2C not configured.'))
    }

    return this._prepareAuthorizationRedirect()
      .then(authorizeUrl => {
        window.location.href = authorizeUrl
      })
  }

  handleRedirect () {
    return this._ensureRedirectHandled()
  }

  _prepareAuthorizationRedirect () {
    const redirectUri = this._b2cConfig.redirectUri || `${window.location.origin}/redirect`
    const scopes = this._getLoginScopes().join(' ')

    return this._createPkcePair()
      .then(({ verifier, challenge }) => {
        const state = this._createRandomState()
        window.sessionStorage.setItem('b2c.codeVerifier', verifier)
        window.sessionStorage.setItem('b2c.authState', state)

        const params = new URLSearchParams({
          client_id: this._b2cConfig.clientId,
          response_type: 'code',
          redirect_uri: redirectUri,
          response_mode: 'query',
          scope: scopes,
          code_challenge: challenge,
          code_challenge_method: 'S256',
          state
        })

        return `${this._b2cConfig.authority}/oauth2/v2.0/authorize?${params.toString()}`
      })
  }

  _ensureRedirectHandled () {
    if (this._redirectPromise) {
      return this._redirectPromise
    }

    this._redirectPromise = this._$q.resolve()
      .then(() => {
        const currentUrl = new URL(window.location.href)
        const error = currentUrl.searchParams.get('error')
        const errorDescription = currentUrl.searchParams.get('error_description')
        const code = currentUrl.searchParams.get('code')
        const state = currentUrl.searchParams.get('state')

        if (error) {
          const message = errorDescription || error
          return this._$q.reject(new Error(message))
        }

        if (!code) {
          return null
        }

        this._authRedirectInFlight = true

        const expectedState = window.sessionStorage.getItem('b2c.authState')
        if (!expectedState || !state) {
          return this._$q.reject(new Error('Missing authentication state.'))
        }
        if (expectedState !== state) {
          return this._$q.reject(new Error('Authentication state mismatch.'))
        }

        const codeVerifier = window.sessionStorage.getItem('b2c.codeVerifier')
        window.sessionStorage.removeItem('b2c.authState')
        window.sessionStorage.removeItem('b2c.codeVerifier')

        if (!codeVerifier) {
          return this._$q.reject(new Error('Missing PKCE verifier.'))
        }

        currentUrl.search = ''
        window.history.replaceState({}, document.title, `${currentUrl.pathname}${currentUrl.hash}`)

        return this._exchangeAuthCode(code, codeVerifier)
      })
      .finally(() => {
        this._authRedirectInFlight = false
      })
      .catch(err => {
        this._$log.warn('AuthService: Redirect handling failed', err)
        throw err
      })

    return this._redirectPromise
  }

  _isAuthRedirectInFlight () {
    return this._authRedirectInFlight || this._$state.current.name === 'loginRedirect'
  }

  _exchangeAuthCode (code, codeVerifier) {
    const redirectUri = this._b2cConfig.redirectUri || `${window.location.origin}/redirect`

    return this._waitForPrimusConnection()
      .then(() => this._ApiService.call('auth/loginB2C', {
        code,
        redirectUri,
        codeVerifier
      }))
  }

  _waitForPrimusConnection () {
    const status = this._PrimusService.getConnectionStatus()
    if (status === 'connected' || status === 'mock') {
      return this._$q.resolve()
    }

    return this._$q(resolve => {
      const unsubscribe = this._PrimusService.onConnectionStatusChange(nextStatus => {
        if (nextStatus === 'connected' || nextStatus === 'mock') {
          unsubscribe()
          resolve()
        }
      })
    })
  }

  _getLoginScopes () {
    if (Array.isArray(this._b2cConfig.scopes) && this._b2cConfig.scopes.length) {
      return this._b2cConfig.scopes
    }

    if (typeof this._b2cConfig.scopes === 'string' && this._b2cConfig.scopes.trim()) {
      return this._b2cConfig.scopes.split(',').map(scope => scope.trim()).filter(Boolean)
    }

    return ['openid', 'profile', 'email']
  }

  _createPkcePair () {
    const verifier = this._generateCodeVerifier()
    return this._sha256(verifier)
      .then(challenge => ({ verifier, challenge }))
  }

  _generateCodeVerifier () {
    const bytes = new Uint8Array(64)
    window.crypto.getRandomValues(bytes)
    return this._base64UrlEncode(bytes)
  }

  _sha256 (value) {
    const encoder = new TextEncoder()
    return window.crypto.subtle.digest('SHA-256', encoder.encode(value))
      .then(buffer => this._base64UrlEncode(new Uint8Array(buffer)))
  }

  _base64UrlEncode (bytes) {
    let binary = ''
    bytes.forEach(byte => {
      binary += String.fromCharCode(byte)
    })

    return window.btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '')
  }

  _createRandomState () {
    const bytes = new Uint8Array(16)
    window.crypto.getRandomValues(bytes)
    return this._base64UrlEncode(bytes)
  }

  _buildLogoutUrl () {
    if (!this._b2cConfig.authority) {
      return null
    }

    const postLogoutRedirectUri = this._b2cConfig.postLogoutRedirectUri || `${window.location.origin}/login`
    const params = new URLSearchParams({
      post_logout_redirect_uri: postLogoutRedirectUri
    })

    return `${this._b2cConfig.authority}/oauth2/v2.0/logout?${params.toString()}`
  }
}
